import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useData } from "../context/DataContext";
import { resolveImageUrl } from "../admin/ImageUpload";

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isManual = params.get("manual") === "1";
  const { settings } = useData();
  const logo = resolveImageUrl(settings?.logo);
  const [status, setStatus] = useState({ loading: true, paid: false, sepaScheduled: false, mode: null, monthlyAmount: null, iterations: null, message: "Vérification du paiement..." });
  const pollsRef = useRef(0);

  useEffect(() => {
    // Clear the Stripe retry context on any success/manual arrival
    try { sessionStorage.removeItem("elan_stripe_retry"); } catch { /* ignore */ }

    if (isManual) {
      setStatus({ loading: false, paid: false, mode: "manual", message: "Inscription enregistrée. Merci d'apporter votre règlement au prochain forum ou lors d'une permanence." });
      return;
    }
    if (!sessionId) {
      setStatus({ loading: false, paid: false, message: "Session de paiement introuvable." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        // CB: mode==='card' → paid means done
        if (data.mode === "card" && data.paymentStatus === "paid") {
          setStatus({ loading: false, paid: true, mode: "card", message: "Paiement confirmé ! Merci pour votre inscription." });
          return;
        }
        // SEPA: mode==='monthly' → success = schedule created (status complete on Stripe = setup done)
        if (data.mode === "monthly" && (data.status === "complete" || data.subscriptionScheduleId)) {
          setStatus({
            loading: false, paid: false, sepaScheduled: true, mode: "monthly",
            monthlyAmount: data.monthlyAmount, iterations: data.iterations || 8,
            message: "Mandat SEPA validé.",
          });
          return;
        }
        if (data.status === "expired") {
          setStatus({ loading: false, paid: false, message: "La session de paiement a expiré." });
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setStatus({ loading: false, paid: false, message: "Le paiement est en cours de traitement. Vous recevrez une confirmation par email." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        setStatus({ loading: false, paid: false, message: "Une erreur est survenue lors de la vérification." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, isManual]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 sm:p-10 text-center">
        {logo && <img src={logo} alt="L'Élan Danse" className="h-16 mx-auto mb-6 object-contain" />}
        {status.loading ? (
          <>
            <Loader2 size={56} className="mx-auto text-deep-rose animate-spin" />
            <h1 className="font-serif-display text-2xl text-stone-800 mt-5">{status.message}</h1>
            <p className="text-stone-500 text-sm mt-2">Merci de patienter quelques instants...</p>
          </>
        ) : status.paid ? (
          <>
            <CheckCircle2 size={64} className="mx-auto text-emerald-500" />
            <h1 className="font-serif-display text-3xl text-stone-800 mt-5">Paiement confirmé&nbsp;!</h1>
            <p className="text-stone-600 mt-3">{status.message}</p>
            <p className="text-stone-500 text-sm mt-3">Un email récapitulatif suivra. Vous pouvez dès maintenant retrouver vos informations dans votre espace famille.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link to="/espace-famille/login" className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-coral text-white text-sm tracking-widest uppercase">Espace famille</Link>
              <Link to="/" className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-stone-100 text-stone-700 text-sm tracking-widest uppercase">Retour au site</Link>
            </div>
          </>
        ) : status.sepaScheduled ? (
          <>
            <CheckCircle2 size={64} className="mx-auto text-emerald-500" />
            <h1 className="font-serif-display text-3xl text-stone-800 mt-5">Mandat SEPA validé&nbsp;!</h1>
            <p className="text-stone-600 mt-3">Merci&nbsp;! Votre IBAN est enregistré et votre mandat SEPA signé.</p>
            {status.monthlyAmount && (
              <p className="text-stone-700 mt-3">
                Les <strong>{status.iterations || 8} prélèvements</strong> de <strong>{Number(status.monthlyAmount).toFixed(2).replace(".", ",")}&nbsp;€/mois</strong> seront effectués automatiquement chaque mois par Stripe. Aucune action supplémentaire de votre part.
              </p>
            )}
            <p className="text-stone-500 text-sm mt-3">Vous recevrez un email de confirmation avant chaque prélèvement.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link to="/espace-famille/login" className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-coral text-white text-sm tracking-widest uppercase">Espace famille</Link>
              <Link to="/" className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-stone-100 text-stone-700 text-sm tracking-widest uppercase">Retour au site</Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-2xl text-stone-800">Statut du paiement</h1>
            <p className="text-stone-600 mt-3">{status.message}</p>
            <Link to="/" className="inline-flex mt-6 px-6 py-2.5 rounded-full bg-stone-100 text-stone-700 text-sm tracking-widest uppercase">Retour au site</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
